import {
  Canvas,
  Circle,
  Polygon,
  Line,
  Point,
  util
} from 'fabric';
import { useEffect } from 'react';

type PointPoly = { x: number; y: number };

interface CustomPolygon extends Polygon {
  reenterEditMode: () => void;
  exitEditMode: () => void;
}

interface UseMapDrawingLayerProps  {
  canvasRef: React.RefObject<HTMLCanvasElement>;
  mode: "reset" | "polygon" | "edit";
  polygonRef: React.MutableRefObject<Polygon | null>;
  valueColor?: string;
  onPolygonComplete?: (points: PointPoly[]) => void;
  onPolygonEditExit?: () => void;
  onVertexRightClick: (vertexIndex: any, pointer: any) => void;
  initialPolygon?: {
    points: PointPoly[];
    color?: string;
  };
  prefillVersion?: number;
}

export const useMapDrawingLayer = ({
  canvasRef,
  mode,
  polygonRef,
  valueColor,
  onPolygonComplete,
  onPolygonEditExit,
  onVertexRightClick,
  initialPolygon,
  prefillVersion = 0
}: UseMapDrawingLayerProps ) => {

  let isAddingVertex = false;


  useEffect(() => {
    if (mode !== "polygon") return;

    if (!canvasRef.current) return;

    const canvas = new Canvas(canvasRef.current, {
      selection: false,
      preserveObjectStacking: true,
    });

    let points: PointPoly[] = [];
    let circles: Circle[] = [];
    let lines: Line[] = [];
    let polygon: Polygon | null = null;
    let isPolygonClosed = false;
    const DOUBLE_CLICK_DELAY = 250;
    let lastPointerDownTimestamp = 0;

    const emitPolygonPoints = () => {
      if (!polygon || !polygon.points || !onPolygonComplete) {
        return;
      }

      const matrix = polygon.calcTransformMatrix();
      const offsetX = polygon.pathOffset?.x ?? 0;
      const offsetY = polygon.pathOffset?.y ?? 0;

      const absolutePoints = polygon.points.map((p) => {
        const absolutePoint = util.transformPoint(
          new Point(p.x - offsetX, p.y - offsetY),
          matrix
        );
        return { x: absolutePoint.x, y: absolutePoint.y };
      });

      onPolygonComplete(absolutePoints);
    };

    // Aggiunge un punto e crea il cerchio corrispondente
    const addPoint = (pointer: PointPoly) => {
      if (isPolygonClosed) return;

      points.push(pointer);

      const circle = new Circle({
        left: pointer.x,
        top: pointer.y,
        radius: 7,
        fill: "#1A8894",
        originX: "center",
        originY: "center",
        hasControls: false,
        hasBorders: false,
        selectable: true,
        hoverCursor: "pointer",
      });

      circle.on("moving", (i: any) => {
        if (!polygon) return;

        const angle = (polygon.angle ?? 0) * (Math.PI / 180);
        const cx = polygon.left!;
        const cy = polygon.top!;

        const dx = circle.left! - cx;
        const dy = circle.top! - cy;

        const localX = dx * Math.cos(-angle) - dy * Math.sin(-angle);
        const localY = dx * Math.sin(-angle) + dy * Math.cos(-angle);

        // Modifica direttamente i punti del poligono
        polygon.points![i].x = localX;
        polygon.points![i].y = localY;

        // ✅ Ricalcola width/height in base ai nuovi punti
        const xs = polygon.points!.map(p => p.x);
        const ys = polygon.points!.map(p => p.y);
        const minX = Math.min(...xs);
        const minY = Math.min(...ys);
        const maxX = Math.max(...xs);
        const maxY = Math.max(...ys);

        polygon.set({
          width: maxX - minX,
          height: maxY - minY,
          dirty: true,
        });

        polygon.setCoords();
        updateCirclesPosition();

        emitPolygonPoints();
      });


      canvas.add(circle);
      circles.push(circle);

      // Se non è il primo punto crea la linea tra questo e il precedente
      if (points.length > 1) {
        const prev = points[points.length - 2];
        const line = new Line(
          [prev.x, prev.y, pointer.x, pointer.y],
          {
            stroke: "#1A8894",
            selectable: false,
            evented: false,
          }
        );
        lines.push(line);
        canvas.add(line);
      }
    }

    const rollbackLastPoint = () => {
      if (!points.length || isPolygonClosed) {
        return;
      }

      points.pop();

      const lastCircle = circles.pop();
      if (lastCircle) {
        canvas.remove(lastCircle);
      }

      const lastLine = lines.pop();
      if (lastLine) {
        canvas.remove(lastLine);
      }

      canvas.requestRenderAll();
    };

    let isPolygonEditing = false;

    // Chiude il poligono, rimuove linee e cerchi e crea il poligono editabile
    const closePolygon = () => {
      if (points.length < 3 || isPolygonClosed) return;

      isPolygonClosed = true;

      // Rimuovi linee e cerchi
      lines.forEach((line) => canvas.remove(line));
      circles.forEach((circle) => canvas.remove(circle));

      const minX = Math.min(...points.map(p => p.x));
      const minY = Math.min(...points.map(p => p.y));

      const normalizedPoints = points.map(p => ({
        x: p.x - minX,
        y: p.y - minY,
      }));

      polygon = new Polygon(normalizedPoints, {
        fill: valueColor ?? "#1A8894",
        opacity: 0.7,
        stroke: "black",
        strokeWidth: 2,
        objectCaching: false,
        selectable: true,
        originX: 'left',
        originY: 'top',
        left: minX,
        top: minY,
      });

      polygonRef.current = Object.assign(polygon, {
        reenterEditMode,
        exitEditMode,
        enterAddVertexMode,
        updateFillColor: (newColor: string) => {
          polygon!.set('fill', newColor);
          canvas.requestRenderAll();
        },
        removeVertex: (vertexIndex: number) => {
          if (!polygon || polygon.points.length <= 3) return;

          polygon.points.splice(vertexIndex, 1);
          updateCirclesPosition();
          hidePolygonEditHandles();
          showPolygonEditHandles();
          emitPolygonPoints();
          canvas.requestRenderAll();
        }
      }) as CustomPolygon & { 
        enterAddVertexMode: (p: PointPoly) => void 
        updateFillColor: (color: string) => void;
      };

      canvas.add(polygon);

      // Aggiorna cerchi su movimento
      polygon.on('moving', updateCirclesPosition);

      // Aggiorna cerchi durante lo scaling in tempo reale
      polygon.on('scaling', hideCirclesTemporarily);

      // Durante rotazione
      polygon.on('rotating', hideCirclesTemporarily);

      // Dopo qualsiasi trasformazione (es. scaling completato)
      polygon.on('modified', () => {
        if (!polygon) return;

        //console.log('[modified] - Bounding box before normalize:', polygon.getBoundingRect());

        updateCirclesPosition(); // <- aggiorna posizione con nuova scala/rotazione 
        if (isPolygonEditing) {
          hidePolygonEditHandles(); // rimuovi cerchi precedenti (se presenti)
          showPolygonEditHandles(); // ricrea i cerchi dai punti attuali del poligono
        }

        emitPolygonPoints();
      });

      canvas.on('mouse:dblclick', (e) => {
        const target = e.target;
        if (target !== polygon) {
          return;
        }

        // In edit mode, double-click on polygon closes editing
        // so external map pan can be used immediately.
        if (isPolygonEditing) {
          exitEditMode();
          return;
        }

        // If polygon is already closed but not in edit, double-click re-enters edit mode.
        if (isPolygonClosed) {
          isPolygonEditing = true;
          showCirclesAndUpdate();
          showPolygonEditHandles();
        }
      });

      emitPolygonPoints();

      isPolygonEditing = true;
    }

    const updateCirclesPosition = () => {
      if (!polygon) return;

      const angle = (polygon.angle ?? 0) * (Math.PI / 180);
      const scaleX = polygon.scaleX ?? 1;
      const scaleY = polygon.scaleY ?? 1;
      const cx = polygon.left!;
      const cy = polygon.top!;

      circles.forEach((circle, i) => {
        const point: any = polygon?.points![i];
        if (!point) return;

        // Applica scala e rotazione per ottenere coordinate globali
        const rotatedX = point.x * scaleX * Math.cos(angle) - point.y * scaleY * Math.sin(angle);
        const rotatedY = point.x * scaleX * Math.sin(angle) + point.y * scaleY * Math.cos(angle);

        circle.set({
          left: cx + rotatedX,
          top: cy + rotatedY,
        });

        circle.setCoords();
      });


      canvas.requestRenderAll();
    };

    const showPolygonEditHandles = () => {
      if (!polygon) return;

      canvas.selection = false;

      // Rimuove tutti i cerchi precedenti dal canvas
      circles.forEach(c => canvas.remove(c));
      circles = [];

      const matrix = polygon.calcTransformMatrix();
      const points = polygon.points!;

      circles = points.map((p, i) => {
        // Trasforma i punti nel sistema globale (se poligono scalato)
        const global = util.transformPoint(
          new Point(p.x - (polygon?.pathOffset?.x ?? 0), p.y - (polygon?.pathOffset.y ?? 0)),
          matrix
        );

        const circle = new Circle({
          left: global.x,
          top: global.y,
          radius: 6,
          fill: "white",
          stroke: "black",
          originX: "center",
          originY: "center",
          hasControls: false,
          hasBorders: false,
          selectable: true,
          hoverCursor: "pointer",
          evented: true,
        });

        circle.on("moving", () => {
          if (!polygon) return;

          const dx = circle.left! - polygon.left!;
          const dy = circle.top! - polygon.top!;

          const angle = (polygon.angle ?? 0) * (Math.PI / 180);
          const scaleX = polygon.scaleX ?? 1;
          const scaleY = polygon.scaleY ?? 1;

          // Trasforma coordinate globali in locali (relative a pathOffset)
          const localX = (dx * Math.cos(-angle) - dy * Math.sin(-angle)) / scaleX;
          const localY = (dx * Math.sin(-angle) + dy * Math.cos(-angle)) / scaleY;

          polygon.points![i].x = localX;
          polygon.points![i].y = localY;

          // Calcola i nuovi bounds
          const xs = polygon.points!.map(p => p.x);
          const ys = polygon.points!.map(p => p.y);
          const minX = Math.min(...xs);
          const minY = Math.min(...ys);
          const maxX = Math.max(...xs);
          const maxY = Math.max(...ys);

          const width = maxX - minX;
          const height = maxY - minY;

          // Ri-centra i punti per pathOffset = center del bounding box
          polygon.points = polygon.points!.map(p => ({
            x: p.x - minX,
            y: p.y - minY,
          }));

          // Calcola delta per compensare la posizione globale
          // Trasforma delta locale (minX, minY) in delta globale considerando rotazione e scala
          const offsetX = minX * scaleX * Math.cos(angle) - minY * scaleY * Math.sin(angle);
          const offsetY = minX * scaleX * Math.sin(angle) + minY * scaleY * Math.cos(angle);

          polygon.set({
            left: polygon.left! + offsetX,
            top: polygon.top! + offsetY,
            width,
            height,
            pathOffset: new Point(width / 2, height / 2),
          });

          polygon.setCoords();
          canvas.requestRenderAll();

          emitPolygonPoints();
        });

        circle.on('contextmenu', (e: any) => {
          if (e.e.button === 2) { // right-click
            e.e.preventDefault();
            e.e.stopPropagation();

            const circleCenter = circle.getCenterPoint(); // posizione assoluta nel canvas fabric (dopo trasformazioni)

            // Ottieni il bounding rect del canvas (in viewport coordinates)
            const canvasRect = canvas.getElement().getBoundingClientRect();

            // ATTENZIONE: devi dividere la dimensione reale canvas.width per canvasRect.width per ottenere lo scale attuale
            const scaleX = canvasRect.width / canvas.getWidth();
            const scaleY = canvasRect.height / canvas.getHeight();

            // Ora il punto circleCenter deve essere scalato come il canvas visivamente appare
            const scaledX = circleCenter.x * scaleX;
            const scaledY = circleCenter.y * scaleY;

            const clientX = canvasRect.left + scaledX;
            const clientY = canvasRect.top + scaledY;

            if (onVertexRightClick) {
                onVertexRightClick(i, { x: clientX, y: clientY });
            }
          }
        });

        canvas.add(circle);
        return circle;
      });

      canvas.requestRenderAll();
    };

    const hidePolygonEditHandles = () => {
      isPolygonEditing = false;
      circles.forEach(c => canvas.remove(c));
      circles = [];
      canvas.requestRenderAll();
    };

    const hideCirclesTemporarily = () => {
      circles.forEach(c => c.set({ visible: false }));
    };

    const showCirclesAndUpdate = () => {
      updateCirclesPosition(); // <- aggiorna posizione con nuova scala/rotazione
      circles.forEach(c => c.set({ visible: true }));
      canvas.requestRenderAll();
    };

    // Click fuori dal poligono
    canvas.on('mouse:down', (opt) => {

      const evt = opt.e as MouseEvent;
      const pointer = canvas.getPointer(evt);
      const target = opt.target;

      if (isAddingVertex && polygon) {
        isAddingVertex = false;
        canvas.defaultCursor = 'default';

        // Trova la posizione nel poligono dove inserire il nuovo punto
        insertVertexAtClosestEdge(pointer);

        showPolygonEditHandles(); // ricrea i cerchi
        emitPolygonPoints();

        return;
      }

      // Aggiunta o chiusura con doppio click sinistro (solo se il poligono non è chiuso)
      if (evt.button === 0 && !isPolygonClosed) {
        const now = Date.now();
        const isDoubleClick = now - lastPointerDownTimestamp <= DOUBLE_CLICK_DELAY;
        lastPointerDownTimestamp = now;

        if (isDoubleClick) {
          rollbackLastPoint();
          closePolygon();
          return;
        }

        addPoint(pointer);
        return;
      }

      // Click outside polygon in edit mode closes edit mode.
      if (isPolygonEditing && target !== polygon && !circles.includes(target as Circle)) {
        hidePolygonEditHandles();
      }
      if (!isPolygonEditing && target !== polygon && !circles.includes(target as Circle)) {
        onPolygonEditExit?.(); // <-- QUI si resetta il mode
      }
    });

    const reenterEditMode = () => {
      if (polygon && isPolygonClosed) {
        isPolygonEditing = true;
        showCirclesAndUpdate();
        showPolygonEditHandles();
      }
    };

    const exitEditMode = () => {
      if (polygon && isPolygonEditing) {
        hidePolygonEditHandles();
        if (onPolygonEditExit) {
          onPolygonEditExit();
        }
      }
    };

    const enterAddVertexMode = () => {
      if (polygon && isPolygonClosed) {
        isAddingVertex = true;
        canvas.defaultCursor = 'crosshair';
      }
    };

    const hydratePolygonFromInitialPoints = () => {
      if (!initialPolygon?.points || initialPolygon.points.length < 3) {
        return;
      }

      if (prefillVersion <= -1) {
        return;
      }

      points = initialPolygon.points.map((point) => ({ x: point.x, y: point.y }));
      closePolygon();
      reenterEditMode();
    };

    hydratePolygonFromInitialPoints();

    const insertVertexAtClosestEdge = (pointer: PointPoly) => {
      if (!polygon || !polygon.points) return;

      const angle = (polygon.angle ?? 0) * (Math.PI / 180);
      const scaleX = polygon.scaleX ?? 1;
      const scaleY = polygon.scaleY ?? 1;

      // Trasforma il punto cliccato da coordinate assolute a coordinate locali del poligono
      const dx = pointer.x - polygon.left!;
      const dy = pointer.y - polygon.top!;

      const localX = (dx * Math.cos(-angle) - dy * Math.sin(-angle)) / scaleX;
      const localY = (dx * Math.sin(-angle) + dy * Math.cos(-angle)) / scaleY;

      const localPointer = { x: localX, y: localY };

      const points = polygon.points!;
      let closestIndex = -1;
      let minDistance = Infinity;

      for (let i = 0; i < points.length; i++) {
        const current = points[i];
        const next = points[(i + 1) % points.length]; // poligono chiuso

        // Calcola distanza dal punto locale al segmento corrente (tutti in coordinate locali)
        const distance = pointToSegmentDistance(localPointer, current, next);

        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = i + 1;
        }
      }

      // Inserisce il nuovo punto in coordinate locali
      polygon.points.splice(closestIndex, 0, localPointer);

      // Aggiorna bounding box, pathOffset e ricalcola bounding box
      updatePolygonBoundingBox();

      canvas.requestRenderAll();
    };

    const pointToSegmentDistance = (p: PointPoly, a: PointPoly, b: PointPoly) => {
      // p, a, b sono in coordinate locali (relative al poligono)
      const px = p.x, py = p.y;
      const x1 = a.x, y1 = a.y;
      const x2 = b.x, y2 = b.y;

      const dx = x2 - x1;
      const dy = y2 - y1;

      const lengthSquared = dx * dx + dy * dy;
      if (lengthSquared === 0) return Math.hypot(px - x1, py - y1);

      let t = ((px - x1) * dx + (py - y1) * dy) / lengthSquared;
      t = Math.max(0, Math.min(1, t));

      const projX = x1 + t * dx;
      const projY = y1 + t * dy;

      return Math.hypot(px - projX, py - projY);
    };

    const updatePolygonBoundingBox = () => {
      if (!polygon || !polygon.points) return;

      const xs = polygon.points.map(p => p.x);
      const ys = polygon.points.map(p => p.y);

      const minX = Math.min(...xs);
      const minY = Math.min(...ys);
      const maxX = Math.max(...xs);
      const maxY = Math.max(...ys);

      const width = maxX - minX;
      const height = maxY - minY;

      // Ri-centra i punti
      polygon.points = polygon.points.map(p => ({
        x: p.x - minX,
        y: p.y - minY,
      }));

      polygon.set({
        left: polygon.left! + minX * (polygon.scaleX ?? 1),
        top: polygon.top! + minY * (polygon.scaleY ?? 1),
        width,
        height,
        pathOffset: new Point(width / 2, height / 2),
      });

      polygon.setCoords();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isPolygonEditing) {
        hidePolygonEditHandles();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      canvas.dispose();
    };

  }, [mode, canvasRef.current, prefillVersion]);
}
